import { ajvResolver } from '@hookform/resolvers/ajv'
import { ContentCopy, NavigateBefore, NavigateNext } from '@mui/icons-material'
import { Alert, Box, TextField } from '@mui/material'
import { AxiosError } from 'axios'
import { Form } from 'components/form'
import { ICheckBoxOptions } from 'components/form/form-radio'
import Header from 'components/header'
import { HeaderModule } from 'components/header-module'
import Loading from 'components/loading'
import Page from 'components/page'
import MailSendSVG from 'components/svg/mail-send'
import Wrapper from 'components/wrapper'
import { useAlert } from 'contexts/alert'
import { ICustomerGeneralDTO } from 'data/dtos/operation/i-customer-dto'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CustomerSchema, { ICustomerFormData } from 'schema/operation/customer'
import api from 'services/api'
import ajv from 'utils/ajv'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}
interface IZodPasswordError {
  value: string
  path: string
  type: string
  errors: string[]
  params: any
  inner: any[]
  name: string
  message: string
}

interface IAlert {
  message: string
  severity: 'success' | 'error' | 'warning' | 'info'
  isOpen: boolean
}

const steps: string[] = ['Adicionar Empresa', 'Adicionar Usuário', 'Convidar']

const checkBoxOptions: ICheckBoxOptions[] = [
  {
    label: 'Permitir que o usuário crie sua senha',
    value: 'createPassword',
  },
  // {
  //   label: 'Gerar automaticamente uma senha forte com 16 caracteres',
  //   value: 'generatePassword',
  // },
  // {
  //   label: 'Criar uma senha',
  //   value: 'sendPassword',
  // },
]

const passwordGenerator = () => {
  const word = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()'
  let password = ''
  for (let i = 0; i < 16; i++) {
    const randomIndex = Math.floor(Math.random() * word.length)
    password += word[randomIndex]
  }
  return password
}

const CustomerForm = () => {
  const [inviteLink, setInviteLink] = useState<string>('')
  const [customerDTO, setCustomerDTO] = useState<ICustomerGeneralDTO>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [readonly, setReadonly] = useState<boolean>(false)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [alreadyInvited, setAlreadyInvited] = useState<boolean>(false)
  const [emailCheckLoading, setEmailCheckLoading] = useState<boolean>(false) // Novo estado para controlar a verificação de email
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const history = useHistory()
  const classes = useStyles()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch,
    setError,
    clearErrors,
    setValue,
    getValues,
  } = useForm<ICustomerFormData>({
    resolver: ajvResolver(CustomerSchema),
    defaultValues: {
      id: '',
      federalTaxId: '',
      corporateName: '',
      tradingName: '',
      invitedEmail: '',
      invitedName: '',
      invitedPhone: '',
      accessPermission: 'Proprietário',
      passwordType: 'createPassword',
    },
  })

  const passwordType = watch('passwordType')

  const handleChange = (formField: any) => closeAlert()

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleNext = async () => {
    if (activeStep === 0) {
      await cnpjAlreadyExistsValidation()
    } else if (activeStep === 1) {
      try {
        if (customerDTO.passwordType === 'sendPassword') {
          const validate = ajv.compile(CustomerSchema)

          if (validate(customerDTO)) {
            clearErrors('password')
            setActiveStep((prevActiveStep) => prevActiveStep + 1)
          } else {
            setError('password', {
              type: 'manual',
              message: 'Senha inválida',
            })
          }
          return
        }
        clearErrors('password')
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
      } catch (error: IZodPasswordError | any) {
        setError('password', {
          type: 'manual',
          message: error.message,
        })
        return
      }
    } else if (activeStep === 2) {
      onSubmit(getValues())
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const cnpjAlreadyExistsValidation = async () => {
    await api
      .post('/organizations/federal-tax-id-already-exists', {
        federaTaxId: customerDTO.federalTaxId,
      })
      .then((response) => {
        const { data } = response.data

        if (data.alreadyExists) {
          setError('federalTaxId', {
            type: 'manual',
            message: 'Este CNPJ já foi convidado para a plataforma!',
          })
        } else {
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
      })
      .catch((error) => {
        openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
      })
  }

  const emailAlreadyRegistered = async () => {
    if (activeStep === 1) {
      setEmailCheckLoading(true)
      try {
        const { data } = await api.put('/invites/email-already-invited', {
          email: customerDTO.invitedEmail,
        })

        setAlreadyInvited(data.data.alreadyInvited)
      } catch (error) {
        console.log(error)
      } finally {
        setEmailCheckLoading(false)
      }
    }
  }

  const onSubmit = useCallback(async (data: ICustomerFormData) => {
    setIsLoading(true)

    const payLoad: ICustomerGeneralDTO = {
      federalTaxId: data.federalTaxId,
      corporateName: data?.corporateName,
      tradingName: data.tradingName,
      invitedPhone: data.invitedPhone,
      invitedEmail: data.invitedEmail,
      invitedName: data.invitedName,
      accessPermission: data.accessPermission,
      password: alreadyInvited ? undefined : data.password,
      passwordType: alreadyInvited ? 'createPassword' : data.passwordType,
    }

    await api
      .post('/organizations/add', payLoad)
      .then((response) => {
        const { data } = response.data

        setInviteLink(data.inviteUrl)
        openAlert('Cliente cadastrado com sucesso!', 'success')
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
        reset()
      })
      .catch((error) => {
        openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
      })
      .finally(() => setIsLoading(false))
  }, [alreadyInvited, openAlert])

  useEffect(() => {
    async function loadData() {
      const { id } = params

      await api
        .get(`/invites/find-by-output/${id}`)
        .then((response) => {
          const { data } = response.data

          const customerResult = {
            federalTaxId: data.organizationOutput.federalTaxId,
            corporateName: data.organizationOutput?.corporateName,
            tradingName: data.organizationOutput.tradingName,
            invitedEmail: data.invitedEmail,
            invitedName: data.invitedName,
            invitedPhone: data.organizationOutput.telephone,
          }

          return reset(customerResult)
        })
        .catch((error) => {
          const err = error as AxiosError

          if (err.response?.status === 404) {
            openAlert('Dados da Organização Ainda não podem ser Editados', 'error')
            history.push('/customers')
          }

          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  useEffect(() => {
    emailAlreadyRegistered()
  }, [activeStep === 1])

  useEffect(() => {
    setCustomerDTO(control._formValues)
  }, [control])

  useEffect(() => {
    if (passwordType === 'generatePassword') {
      setValue('password', passwordGenerator())
    } else if (passwordType === 'createPassword') {
      setValue('password', '')
    } else if (passwordType === 'sendPassword') {
      setValue('password', '')
    }
  }, [passwordType])

  return (
    <Wrapper>
      <Header>
        <HeaderModule.title
          text='Adicionar um novo Cliente'
          backRoute='/customers'
        />
      </Header>
      <Page>
        <Form.Root onHandleSubmit={handleSubmit(handleNext)}>
          <Form.Stepper
            activeStep={activeStep}
            steps={steps}
          />
          {activeStep === 3 ? (
            <Box className={classes.mailContainer}>
              <MailSendSVG className={classes.mailContainerImg} />
              <h1>
                O convite foi enviado com sucesso para o e-mail <span>{customerDTO.invitedEmail}</span>. Ela receberá um
                e-mail com instruções para completar o cadastro e aceitar o convite. Assim que o convite for aceito, a
                empresa será adicionada à plataforma e a conta de usuário estará pronta para uso.
              </h1>
              <Box sx={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <Form.Button
                  label='VER LISTA DE CLIENTES'
                  type='button'
                  variant='text'
                  onClick={() => history.push('/customers')}
                />
                <Form.Button
                  label='ADICIONAR OUTRO CLIENTE'
                  type='button'
                  variant='contained'
                  onClick={() => window.location.reload()}
                />
              </Box>
              <TextField
                label='Compatilhar Link do Convite'
                className={classes.inputInvite}
                disabled={true}
                value={inviteLink}
                InputProps={{
                  endAdornment: (
                    <ContentCopy
                      sx={{ cursor: 'pointer', marginLeft: '1rem', opacity: 0.5 }}
                      onClick={() => {
                        openAlert('Link copiado para a área de transferência!', 'success')
                        navigator.clipboard.writeText(inviteLink)
                      }}
                    />
                  ),
                }}
              />
            </Box>
          ) : (
            <>
              <Form.Body>
                {activeStep === 0 && (
                  <>
                    <Form.SectionText message='Informações sobre a empresa' />

                    <Form.Input
                      label='CNPJ'
                      error={!!errors.federalTaxId}
                      errorMessage={errors?.federalTaxId?.message}
                      disabled={isLoading || readonly}
                      maxLength={20}
                      sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                      name='federalTaxId'
                      rules={{ onChange: (e) => handleChange(e) }}
                      register={register}
                    />

                    <Form.Input
                      label='Razão Social'
                      error={!!errors?.corporateName}
                      errorMessage={errors?.corporateName?.message}
                      disabled={isLoading || readonly}
                      sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                      maxLength={115}
                      name='corporateName'
                      rules={{ onChange: (e) => handleChange(e) }}
                      register={register}
                    />

                    <Form.Input
                      label='Nome Fantasia'
                      error={!!errors.tradingName}
                      errorMessage={errors?.tradingName?.message}
                      disabled={isLoading || readonly}
                      sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                      maxLength={115}
                      name='tradingName'
                      rules={{ onChange: (e) => handleChange(e) }}
                      register={register}
                    />

                    <Form.SectionText message='Sobre o reponsável pela empresa' />

                    <Form.Input
                      label='Nome'
                      error={!!errors.invitedName}
                      errorMessage={errors?.invitedName?.message}
                      disabled={isLoading || readonly}
                      maxLength={200}
                      sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                      name='invitedName'
                      rules={{ onChange: (e) => handleChange(e) }}
                      register={register}
                    />

                    <Form.Input
                      label='E-mail'
                      error={!!errors.invitedEmail}
                      errorMessage={errors?.invitedEmail?.message}
                      disabled={isLoading || readonly}
                      sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                      maxLength={200}
                      name='invitedEmail'
                      rules={{ onChange: (e) => handleChange(e) }}
                      register={register}
                    />

                    <Form.Input
                      label='Telefone'
                      type='tel'
                      control={control}
                      error={!!errors.invitedPhone}
                      errorMessage={errors?.invitedPhone?.message}
                      disabled={isLoading || readonly}
                      sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                      maxLength={30}
                      name='invitedPhone'
                    />
                  </>
                )}

                {activeStep === 1 && (
                  <>
                    {emailCheckLoading ? (
                      <Loading />
                    ) : (
                      <>
                        <Form.SectionText message='Informações sobre o responsável da conta Conducco' />

                        <Form.Input
                          label='Nome'
                          error={!!errors.invitedName}
                          errorMessage={errors?.invitedName?.message}
                          disabled={true}
                          maxLength={20}
                          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                          name='invitedName'
                          rules={{ onChange: (e) => handleChange(e) }}
                          register={register}
                        />

                        <Form.Input
                          label='E-mail'
                          error={!!errors.invitedEmail}
                          helperText='O usuário fará login com este e-mail.'
                          errorMessage={errors?.invitedEmail?.message}
                          disabled={true}
                          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                          maxLength={115}
                          name='invitedEmail'
                          rules={{ onChange: (e) => handleChange(e) }}
                          register={register}
                        />

                        <Form.Input
                          label='Permissão de acesso'
                          error={!!errors.accessPermission}
                          errorMessage={errors?.accessPermission?.message}
                          disabled={true}
                          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                          maxLength={115}
                          name='accessPermission'
                          rules={{ onChange: (e) => handleChange(e) }}
                          register={register}
                        />

                        <Form.Input
                          label='Telefone'
                          error={!!errors.invitedPhone}
                          errorMessage={errors?.invitedPhone?.message}
                          disabled={true}
                          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                          maxLength={115}
                          name='invitedPhone'
                          rules={{ onChange: (e) => handleChange(e) }}
                          register={register}
                        />

                        {!alreadyInvited && (
                          <>
                            <Form.Divisor
                              marginTop='1rem'
                              marginBottom='0rem'
                            />
                            <Form.SectionText message='Criar uma senha' />
                            <Form.Radio
                              name='passwordType'
                              control={control}
                              options={checkBoxOptions}
                              sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                            />

                            {watch('passwordType') !== 'createPassword' && (
                              <Form.Input
                                label='Senha'
                                error={!!errors.password}
                                errorMessage={errors?.password?.message}
                                disabled={watch('passwordType') === 'generatePassword'}
                                sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                                maxLength={115}
                                name='password'
                                rules={{ onChange: (e) => handleChange(e) }}
                                register={register}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {activeStep === 2 && (
                  <Box className={classes.container}>
                    <Form.SectionText message='Informações sobre a empresa' />

                    <Box className={classes.infoContainer}>
                      <h1>
                        CNPJ: <span>{customerDTO.federalTaxId}</span>
                      </h1>
                      <h1>
                        Razão Social: <span>{customerDTO?.corporateName}</span>
                      </h1>
                      <h1>
                        Nome Fantasia: <span>{customerDTO.tradingName}</span>
                      </h1>
                    </Box>

                    {/* <Form.SectionText message='Informações sobre o responsável pela empresa' />

                    <Box className={classes.infoContainer}>
                      <h1>
                        Nome: <span>{customerDTO.invitedName}</span>
                      </h1>
                      <h1>
                        E-mail: <span>{customerDTO.invitedEmail}</span>
                      </h1>
                      <h1>
                        Telefone: <span>{customerDTO.invitedPhone} </span>
                      </h1>
                    </Box> */}

                    <Form.SectionText message='Informações sobre o responsável pela conta Conducco' />

                    <Box className={classes.infoContainer}>
                      <h1>
                        Nome: <span>{customerDTO.invitedName}</span>
                      </h1>
                      <h1>
                        E-mail: <span>{customerDTO.invitedEmail}</span>
                      </h1>
                      <h1>
                        Permissão de acesso: <span>{customerDTO.accessPermission}</span>
                      </h1>
                      <h1>
                        Telefone: <span>{customerDTO.invitedPhone}</span>{' '}
                      </h1>
                    </Box>

                    {watch('passwordType') === 'sendPassword' ? (
                      <Alert
                        severity='info'
                        className={classes.alert}
                      >
                        Após entrar na plataforma, o responsável {customerDTO.invitedName} deve aceitar os termos de uso
                        e a política de privacidade da Conducco. Uma vez concluído o cadastro, o responsável terá acesso
                        à plataforma e poderá prosseguir com a configuração do seu cofre digital.
                      </Alert>
                    ) : (
                      <Alert
                        severity='info'
                        className={classes.alert}
                      >
                        O responsável {customerDTO.invitedName} receberá um e-mail no endereço{' '}
                        {customerDTO.invitedEmail} com um link para acessar a tela de criação de nova senha e, em
                        seguida, aceitar os termos de uso e a política de privacidade da Conducco. Após concluir o
                        cadastro, o responsável poderá acessar a plataforma e dar continuidade à configuração do seu
                        cofre digital.
                      </Alert>
                    )}
                  </Box>
                )}
              </Form.Body>

              <Form.Divisor marginTop='0rem' />

              <Form.Footer spaceBetween>
                <Box>
                  <Form.Button
                    label='Cancelar'
                    type='cancel'
                    variant='text'
                    isLoading={isLoading}
                  />
                  {!params.id && (
                    <Form.Button
                      label={'Continuar'}
                      type={activeStep === 0 ? 'submit' : 'button'}
                      variant='contained'
                      isLoading={isLoading}
                      isDisabled={isLoading}
                      endIcon={<NavigateNext />}
                      {...(activeStep > 0 && { onClick: handleNext })}
                    />
                  )}
                </Box>
                {activeStep !== 0 && (
                  <Form.Button
                    label='Voltar'
                    type='back'
                    variant='text'
                    onClick={handleBack}
                    startIcon={<NavigateBefore />}
                  />
                )}
              </Form.Footer>
            </>
          )}
        </Form.Root>
      </Page>
    </Wrapper>
  )
}

export default CustomerForm
